import React, { FC, useContext } from 'react'

import { useI18n } from '@shopify/react-i18n'
import { Box, Div, LazyBackgroundImage, Row, Text } from '@vividseats/vivid-ui-kit'
import classNames from 'classnames'

import filterContext from '@/components/pages/production/components/context'
import LayoutContext from '@/context/layout/context'
import ListingsContext from '@/context/listings'
import { buildResponsiveHighlightedMapImageUrls } from '@/context/listings/utils'
import { usePromoPrice } from '@/hooks/use-promo-price'
import type { ComposedListing, ListingsGroup } from '@/types/app-types'
import { getListingPrice } from '@/utils/i18n'

import { LISTING_ROW_IMAGE_HEIGHT, LISTING_ROW_IMAGE_WIDTH } from '../../constants'

import styles from './styles.module.scss'

interface Props extends ListingsGroup {
    bestListing: ComposedListing
    showUsd: boolean
    onClick?: () => void
    onMouseEnter?: () => void
}

const ListingsGroupRow: FC<Props> = ({
    bestListing,
    defaultQuantity,
    quantityRangeText,
    sectionLong,
    sectionId,
    sectionColor,
    showUsd,
    onClick,
    onMouseEnter,
    id,
}) => {
    const { state: filterState } = useContext(filterContext)
    const {
        state,
        selectors: { i18nListingsData },
    } = useContext(ListingsContext)
    const {
        state: { data: mapData },
    } = useContext(LayoutContext)

    const [i18n] = useI18n()
    const showSectionSeatView = !!mapData?.jsonFileName

    const listingPrice = filterState.showAllInPrice ? bestListing.allInPrice : bestListing.price
    const showAllInPrice = filterState.showAllInPrice

    const discountPrice = usePromoPrice(
        Number(listingPrice),
        filterState.quantityFilterState.quantity || defaultQuantity,
    )
    const hasDiscountPrice = Boolean(discountPrice)

    const highlightedMapImageUrl = buildResponsiveHighlightedMapImageUrls({
        layoutId: state.data?.global[0].layoutId || '',
        sectionId,
        height: LISTING_ROW_IMAGE_HEIGHT,
        width: LISTING_ROW_IMAGE_WIDTH,
    })

    return (
        <div
            className={styles.listingRowContainer}
            id="row-container"
            data-testid="listing-group-row-container"
            style={{ borderLeft: `6px solid ${showSectionSeatView ? sectionColor : 'transparent'}` }}
        >
            <Row
                role="button"
                align="stretch"
                tabIndex={-1}
                className={styles.listingRow}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                data-testid={id}
                data-sectionid={sectionId}
            >
                {showSectionSeatView && (
                    <Div className={styles.sectionPreview} data-testid="section-preview">
                        <LazyBackgroundImage
                            url={highlightedMapImageUrl}
                            className={styles.image}
                            data-testid={'highlighted-map-image-thumbnail'}
                        />
                    </Div>
                )}
                <Box
                    paddingLeft={3}
                    paddingRight={5}
                    paddingTop={1}
                    paddingBottom={1}
                    className={styles.sectionContent}
                >
                    <Box data-testid={sectionId}>
                        <Text
                            size="md"
                            as="div"
                            weight="regular-bold"
                            className={styles.infoLineHeight}
                            altFont
                            truncate
                        >
                            {sectionLong}
                        </Text>
                        <Box align="center" display="flex" flexDirection="row" id={`section-${id}`} wrap>
                            <Text size="sm" altFont className={classNames(styles.nowrap, styles.infoLineHeight)}>
                                {quantityRangeText}
                            </Text>
                        </Box>
                    </Box>
                </Box>
                <Box className={styles.priceContent} marginRight={3}>
                    <Box>
                        <Text size="sm" altFont className={classNames(styles.nowrap, styles.infoLineHeight)}>
                            {showAllInPrice && <span className={styles.feelsIncluded}>Fees Incl.</span>}
                            {hasDiscountPrice && (
                                <span className={styles.beforeDiscount} data-testid="listing-price">{`${getListingPrice(
                                    i18n,
                                    i18nListingsData,
                                    listingPrice,
                                )}`}</span>
                            )}
                        </Text>
                    </Box>
                    <Box display="flex" align="center">
                        <Text
                            as="span"
                            size="sm"
                            state="disabled"
                            weight="regular-bold"
                            className={styles.from}
                            altFont
                        >
                            From
                        </Text>
                        <Text
                            size="lg"
                            as="span"
                            className={hasDiscountPrice ? styles.discountAmount : styles.priceAmount}
                            data-testid={hasDiscountPrice ? 'discount-price' : 'listing-price'}
                            altFont
                        >
                            {hasDiscountPrice
                                ? getListingPrice(i18n, i18nListingsData, discountPrice)
                                : getListingPrice(i18n, i18nListingsData, listingPrice)}
                        </Text>
                        <Text size="xs" as="span" altFont className={styles.priceUnitStyle} data-testid="price-style">
                            {showUsd && !i18nListingsData?.applyCurrencyConversion ? <>&nbsp;USD</> : null}&nbsp;ea
                        </Text>
                    </Box>
                </Box>
            </Row>
        </div>
    )
}

export default ListingsGroupRow
